import EventEmitter from '../helpers/EventEmmiter';
import HeyGenManager from './HeyGenManager';
import { resolveLink, buildCurrentSceneContext, resolveRoomText, pickOpeningIntro } from './utils/scriptUtils';
import HelpService from 'lib/service/help.service';
import TourState from './TourState';
import ConnectionManager from './ConnectionManager';
import RealtimeEventHandler from './RealtimeEventHandler';
import ToolHandler from './ToolHandler';
import APIController from './api';
import { getAvatarDomain } from './helper.js';
import SessionBuilder from './session/SessionBuilder';
import RAGManager, { queryTerms } from './session/AgenticRAGManager';
import MuseumAudioPlayer from './museum/MuseumAudioPlayer';
import MuseumGuideService from './museum/MuseumGuideService';
import MuseumSceneEndPopup from './museum/MuseumSceneEndPopup';
import MuseumTimeline from './museum/MuseumTimeline';
import FloatingStatusDisplay from './ui/FloatingStatusDisplay';
import AudioMixer from './audio/AudioMixer';
import AvatarAudioBridge from './audio/AvatarAudioBridge';
import ViewerBridge from './ViewerBridge';
import { getMicPreset } from './micSensitivity';

// How long the OpenAI session waits for the video avatar before giving up on
// it and starting voice-only (LiveAvatar new-session + LiveKit join is
// normally a few seconds).
const HEYGEN_CONNECT_TIMEOUT_MS = 15000;

class RealtimeTourGuide extends EventEmitter {
  // Transcript lookups: only for transcripts with this many content words, and
  // used only when this share of the model's query words is in the transcript.
  static PREFETCH_MIN_TERMS = 2;
  static PREFETCH_MIN_OVERLAP = 0.5;

  constructor(language_code, unlockedAudio = null) {
    super();
    this._helperService = new HelpService();
    this._urlParams = this._helperService.urlParams;
    this.statusDisplay = new FloatingStatusDisplay(this._urlParams.showOpenaiPrice);
    this.language = language_code || 'en';
    this.state = new TourState();
    this.state.language = this.language;
    this.mixer = new AudioMixer();
    this.connection = new ConnectionManager(this.state, this.statusDisplay, (e, d) => this._fireEvent(e, d), this.mixer);
    this.bridge = new ViewerBridge(this.state, (e, d) => this._fireEvent(e, d), () => this);
    this.toolHandler = new ToolHandler(this.state, this.connection, (e, d) => this._fireEvent(e, d), this);
    this.toolHandler.bridge = this.bridge;
    this.realtimeHandler = new RealtimeEventHandler(this.statusDisplay, (e, d) => this._fireEvent(e, d), this.toolHandler);
    this.realtimeHandler.send = (msg) => {
      if (this.connection.isInit()) this.connection.dataChannel.send(JSON.stringify(msg));
    };

    this.sessionBuilder = new SessionBuilder(this.state);
    this.rag = new RAGManager({
      getInstructions: () => this.buildSessionInstructions(),
      sendSessionUpdate: (prompt) => this.sendSessionUpdate(prompt),
      createMessage: (role, text) => this._createMessage(role, text),
    });

    // Room audio (non-museum: plays pre-recorded room greeting)
    this._roomAudio = null;
    this._roomAudioPausedBySystem = false;

    // Museum pre-recorded point audio
    // Pass the pre-unlocked Audio element so iOS can reuse it without needing
    // a new user gesture each time a museum point plays.
    this.museumAudioPlayer = new MuseumAudioPlayer(unlockedAudio);
    this._wireMuseumAudioPlayer();

    // Conversation history pruning
    this.MAX_HISTORY = 10;
    this._historyItems = [];

    // Audio session limit (seconds); 100 audio tokens = 1 second
    this.MAX_AUDIO_SECONDS = 20 * 60;
    this._totalAudioSeconds = 0;

    this.on('audio:seconds', (secs) => {
      this._totalAudioSeconds += secs;
      if (this._totalAudioSeconds >= this.MAX_AUDIO_SECONDS) {
        console.warn('[guide] audio limit reached, disconnecting');
        this.disconnect();
      }
    });

    // AI speaking state (used to avoid audio overlap)
    this._aiSpeaking = false;
    this._audioActive = false; // true while a response is generating audio
    this._ragSearchInProgress = false;
    this._lastSessionSig = null;
    this._resumeTimer = null;

    // Idle timeout: disconnect after 5 min of no user speech
    this.IDLE_TIMEOUT_MS = 5 * 60 * 1000;
    this._idleTimer = null;

    // Museum guide service (set when tour type === 'MUSEUM')
    this.guideService = null;
    this._sceneEndPopup = null;

    // HeyGen video avatar (set by initHeyGen())
    this.heygenManager = null;
    this.heygenMode = 'FULL';
    this.audioBridge = null; // LITE mode: OpenAI audio → avatar lip-sync
    this._heygenConnecting = null; // Promise<boolean> from heygenManager.connect()

    this._wireVoiceEvents();

    this.connection.onOpen = () => {
      // Session config is set via /calls FormData — no session.update needed on connect
      console.log('[guide] data channel open, session configured via /calls');
      this._resetIdleTimer();
      if (this.isMuseum()) {
        this._startGuideService(this._avatarContainer);
      } else {
        this._openingIntro();
      }
    };
    this.connection.onMessage = e => this.realtimeHandler.handleRealtimeEvent(e);
  }

  // Room message (video avatar) or room audio when the start room has one,
  // otherwise the model greets — the session used to start silent then.
  _openingIntro() {
    const intro = pickOpeningIntro(this.state.getRoomInfo(), {
      avatar: !!this.heygenManager,
      language: this.bridge.getLanguage(),
      defaultLang: this.state.tourData?.defaultLang || 'en',
    });
    console.log('[guide] opening intro:', intro.kind);
    if (intro.kind === 'avatar-message') {
      if (this.heygenManager.connected) this.heygenManager.speak(intro.text);
      else this._pendingHeyGenMessage = intro.text;
    } else if (intro.kind === 'room-audio') {
      this.play(intro.link);
    } else {
      this._greet();
    }
  }

  _greet() {
    if (!this.connection.isInit()) return;
    const name = `${this.state.tourData?.lead?.first_name || ''}`.trim();
    this.connection.dataChannel.send(JSON.stringify({
      type: 'response.create',
      response: {
        instructions: `Greet the visitor once, in ONE short sentence${name ? `, by their first name (${name})` : ''}, ` +
          `in language = ${this.bridge.getLanguage()}. Then stop and wait for them to speak.`
      }
    }));
  }

  // The visitor asked a question together with a move; the room has loaded
  // now, so answer it (instead of the room intro) with the new room's context.
  answerOnArrival(question) {
    if (!this.connection.isInit() || !question) return;
    this.stopRoomAudio();
    const room = this.state.getRoomInfo();
    console.log('[guide] answering on arrival:', question);
    this.connection.dataChannel.send(JSON.stringify({
      type: 'response.create',
      response: {
        instructions: `${this.buildSessionInstructions()}\n\nYOU HAVE JUST ARRIVED in "${room?.title || room?.name || 'the room'}". ` +
          `Answer the visitor's question now, in 1-3 short sentences, from CURRENT LOCATION above ` +
          `(call 'search_knowledge_base' if it is not there): "${question}". Do not mention the move. ` +
          `Use language = ${this.bridge.getLanguage()}.`
      }
    }));
  }

  // ─── Wiring helpers ──────────────────────────────────────────────────────────

  _wireVoiceEvents() {
    this.on('voice:speaking', () => {
      this._aiSpeaking = true;
      this.stopRoomAudio();
    });

    this.on('audio:delta', () => {
      if (!this._audioActive) this._audioActive = true;
    });

    this.on('audio:seconds', () => {
      this._audioActive = false;
    });

    this.on('voice:done', () => {
      this._aiSpeaking = false;
      if (this.isMuseum() && this.guideService?.isPaused) {
        this._scheduleGuideResume();
      }
    });

    this.on('voice:listening', () => {
      this.stopRoomAudio();
    });

    // user:speech_started fires only on real VAD detection — not on agent-done state.
    // Bluetooth headsets (Sony WH-1000XM, AirPods) set this off without anyone
    // speaking — the switch to the headset's call profile clicks, and the guide's
    // own narration leaks back into the mic — and the museum script used to pause
    // on the very first trigger and stay paused. Now: pause only once speech has
    // lasted the mic preset's barge-in time, ignore transcripts that are just the
    // narration, and resume if no transcript arrives.
    this.on('user:speech_started', () => {
      this._resetIdleTimer();
      clearTimeout(this._resumeTimer);
      if (!this.isMuseum() || !this.guideService || this.guideService.isPaused) return;
      clearTimeout(this._museumPauseTimer);
      // Until the transcript says question or noise, don't start the next point.
      this.guideService.holdAdvance();
      const delay = this.realtimeHandler.micPreset?.bargeInMs || 0;
      if (delay) {
        this._museumPauseTimer = setTimeout(() => this._pauseGuideForSpeech(), delay);
      } else {
        this._pauseGuideForSpeech();
      }
      // Context injection deferred to transcript:user so we can cancel on empty/noise
    });

    // Start the knowledge lookup from the transcript, while the model is still
    // deciding to call search_knowledge_base (see updateRAGContext).
    this.on('timing:turn_start', () => { this._ragPrefetch = null; });
    this.on('transcript:user', (transcript) => this._prefetchRAG(transcript));

    // The turn's words, for checks on a tool call (ambiguous room, question + move)
    this.on('timing:turn_start', () => this.state.startUserTurn());
    this.on('transcript:user', (transcript) => this.state.setUserTranscript(transcript));

    // speech_stopped before the barge-in time = a blip: never pause for it.
    this.on('voice:processing', () => clearTimeout(this._museumPauseTimer));

    // Inject context only after we confirm a real transcript; cancel+resume on noise/echo
    this.on('transcript:user', (transcript) => {
      if (!this.isMuseum() || !this.guideService) return;
      clearTimeout(this._museumPauseTimer);
      clearTimeout(this._speechResumeGuard);
      const text = (transcript || '').trim();
      const echo = text && this._isGuideEcho(text);
      if (text.length < 2 || echo) {
        this.guideService.releaseAdvance(); // noise / echo — the tour carries on
        if (echo) console.log('[guide] ignoring transcript — it is the guide narration picked up by the mic:', text);
        if (this.guideService.isPaused && this._pausedBySpeech) {
          this.stop();
          this._pausedBySpeech = false;
          if (!this._mediaHold) this.guideService.resume(this.bridge.getLanguage());
        } else if (!this.guideService.isPaused && !this._aiSpeaking) {
          this.stop(); // the auto-reply to noise must not talk over the narration
        }
        return;
      }
      // A real question that was shorter than the barge-in time: pause now.
      if (!this.guideService.isPaused) this.guideService.pause();
      this.guideService.releaseAdvance(); // paused already — the run loop stops on its next check
      this._pausedBySpeech = false;
      if (this.connection.isInit()) {
        const context = buildCurrentSceneContext(
          this.state.tourData?.guide || [],
          this.state.currentScene,
          this.state.getRoomInfo(this.state.currentScene),
          this.bridge.getLanguage(),
          this.state.tourData?.defaultLang || 'en'
        );
        this._createMessage('system', `Visitor has a question.${context ? '\n' + context : ''}\nAnswer briefly. Do NOT call on_close — the tour resumes automatically after you answer.`);
      }
    });
  }

  _pauseGuideForSpeech() {
    if (!this.guideService || this.guideService.isPaused) return;
    this.guideService.pause();
    this._pausedBySpeech = true;
    // No transcript (VAD fired on noise and nothing was transcribed): carry on.
    clearTimeout(this._speechResumeGuard);
    this._speechResumeGuard = setTimeout(() => {
      if (!this._pausedBySpeech || !this.guideService?.isPaused || this._aiSpeaking || this._mediaHold) return;
      if (this._ragSearchInProgress || this.realtimeHandler.isAwaitingAnswer()) return;
      console.log('[guide] no transcript after speech start — resuming the tour');
      this.guideService.releaseAdvance();
      this._pausedBySpeech = false;
      this.guideService.resume(this.bridge.getLanguage());
    }, 6000);
  }

  // True when the "visitor" transcript is really the narration that is playing
  // (headset mic picking up the guide's own voice): most of its consecutive word
  // pairs occur in the current or previous point's text. Pairs, not single words,
  // so a real question that reuses narration words ("who was Anne Frank") isn't
  // mistaken for echo.
  _isGuideEcho(transcript) {
    const tokenize = s => (s.toLowerCase().match(/[\p{L}\p{N}]+/gu) || []);
    const words = tokenize(transcript);
    if (words.length < 4) return false;
    const guide = this.state.tourData?.guide || [];
    const { sceneIdx, pointIdx } = this.guideService.getPosition();
    const lang = this.bridge.getLanguage();
    const points = guide[sceneIdx]?.points || [];
    const narration = [points[pointIdx], points[pointIdx - 1]]
      .filter(Boolean)
      .map(p => (p.msg && (p.msg[lang] || Object.values(p.msg).join(' '))) || p.caption?.[lang] || '')
      .join(' ');
    const n = tokenize(narration);
    if (n.length < 2) return false;
    const pairs = new Set();
    for (let i = 0; i < n.length - 1; i++) pairs.add(`${n[i]} ${n[i + 1]}`);
    let hits = 0;
    for (let i = 0; i < words.length - 1; i++) if (pairs.has(`${words[i]} ${words[i + 1]}`)) hits++;
    return hits >= 3 && hits / (words.length - 1) >= 0.5;
  }

  _resetIdleTimer() {
    clearTimeout(this._idleTimer);
    this._idleTimer = setTimeout(() => {
      console.warn('[guide] idle timeout reached, disconnecting');
      this.disconnect();
    }, this.IDLE_TIMEOUT_MS);
  }

  // Visitor-started media (render zone, info-label audio/video, narration) is
  // playing: the AI stops mid-sentence and the museum guide pauses in place.
  // While held, no auto-resume path restarts the guide; releasing resumes it
  // the normal way (not while an answer is still on its way).
  holdForMedia(on) {
    if (on) {
      if (this._mediaHold) return;
      this._mediaHold = true;
      this.stop();
      if (this.isMuseum() && this.guideService && !this.guideService.isPaused) this.guideService.pause();
    } else if (this._mediaHold) {
      this._mediaHold = false;
      if (this.isMuseum() && this.guideService?.isPaused) this._scheduleGuideResume();
    }
  }

  _scheduleGuideResume() {
    clearTimeout(this._resumeTimer);
    this._resumeTimer = setTimeout(() => {
      // The "Let me check." line before a knowledge lookup ends its audio while
      // the lookup (and the real answer) is still on the way — don't restart the
      // museum script in that gap; the answer's own voice:done resumes it.
      if (this._ragSearchInProgress || this.realtimeHandler.isAwaitingAnswer()) return;
      if (this._mediaHold) return; // visitor media is playing — holdForMedia(false) resumes
      if (this.guideService?.isPaused) {
        this.guideService.resume(this.bridge.getLanguage());
      }
    }, 800);
  }

  _wireMuseumAudioPlayer() {
    this.museumAudioPlayer.on('play', () => this.mixer.muteAI());
    this.museumAudioPlayer.on('ended', () => this.mixer.unmuteAI());
    this.museumAudioPlayer.on('interrupted', () => this.mixer.unmuteAI());
    this.museumAudioPlayer.on('error', () => this.mixer.unmuteAI());
  }

  // ─── Public API ───────────────────────────────────────────────────────────────

  isMuseum() {
    return this.state.tourData?.type === 'MUSEUM';
  }

  readyForPlay() {
    return true;
  }

  _resolveLink(linkOrObj) {
    return resolveLink(linkOrObj, this.bridge.getLanguage(), this.state.tourData?.defaultLang || 'en');
  }

  setTourData(tourData) {
    this.state.setTourData(tourData);
  }

  initHeyGen(videoEl, avatarId, quality = 'medium', sandbox = false, mode = 'FULL') {
    this.heygenMode = mode === 'LITE' ? 'LITE' : 'FULL';
    console.log(`[heygen][mode] initHeyGen → ${this.heygenMode} ` +
      (this.heygenMode === 'LITE'
        ? '(OpenAI audio output, bridged to avatar for lip-sync; platform TTS OFF)'
        : '(text output, LiveAvatar platform TTS speaks it)'));
    const manager = new HeyGenManager(videoEl, (e, d) => {
      if (manager !== this.heygenManager) return; // already dropped (fallback/disconnect)
      if (e === 'heygen:disconnected' && d && d.unexpected) {
        // Mid-session room drop: without this the guide keeps sending text
        // (FULL) or bridging audio (LITE) to an avatar that is gone — silence.
        this._fallbackVoiceOnly('LiveKit room disconnected mid-session');
        return;
      }
      if (e === 'avatar:speak_started') this.realtimeHandler.markAudible('avatar');
      if (e === 'heygen:ready' && this._pendingHeyGenMessage) {
        const msg = this._pendingHeyGenMessage;
        this._pendingHeyGenMessage = null;
        this.heygenManager.speak(msg);
      }
      if (e === 'heygen:speak_text') {
        // LITE: no platform TTS — synthesize scripted text through the
        // OpenAI session; the audio reaches the avatar via the bridge
        this.say(d);
        return;
      }
      this._fireEvent(e, d);
    }, this.heygenMode);
    this.heygenManager = manager;
    this.realtimeHandler.heygenManager = this.heygenManager;
    if (this.heygenMode === 'LITE') {
      this.audioBridge = new AvatarAudioBridge(
        (b64) => this.heygenManager && this.heygenManager.speakAudio(b64),
        () => this.heygenManager && this.heygenManager.speakEnd()
      );
      this.realtimeHandler.audioBridge = this.audioBridge;
      this.on('connection:aistream', (stream) => {
        if (!this.audioBridge) return;
        console.log('[heygen][mode] LITE: OpenAI audio stream connected to avatar bridge, speaker output disabled');
        this.mixer.disableAI(); // the voice plays through the avatar, not the speakers
        this.audioBridge.connectStream(stream);
      });
    }
    this._heygenConnecting = this.heygenManager.connect(avatarId, quality, sandbox);
  }

  // Waits (bounded) for the video avatar; on failure or timeout switches the
  // guide to voice-only before the OpenAI session is configured.
  async _settleHeyGen() {
    if (!this.heygenManager || !this._heygenConnecting) return;
    const manager = this.heygenManager;
    let timer;
    const ok = await Promise.race([
      this._heygenConnecting,
      new Promise(resolve => { timer = setTimeout(() => resolve('timeout'), HEYGEN_CONNECT_TIMEOUT_MS); }),
    ]);
    clearTimeout(timer);
    if (manager !== this.heygenManager) return; // disconnected meanwhile
    if (ok === true) return;
    this._fallbackVoiceOnly(ok === 'timeout'
      ? `no connection after ${HEYGEN_CONNECT_TIMEOUT_MS} ms`
      : 'session creation or LiveKit connect failed');
  }

  // RE-V85: the video avatar is unavailable — drop it and let OpenAI speak
  // through the normal speaker path (audio output, mixer enabled). Safe to call
  // more than once. Mid-session it also switches the live session to audio.
  _fallbackVoiceOnly(reason = '') {
    const manager = this.heygenManager;
    if (!manager) return;
    console.warn('[heygen] avatar unavailable — continuing voice-only', reason ? `(${reason})` : '');
    this.heygenManager = null;
    this.realtimeHandler.heygenManager = null;
    this._heygenConnecting = null;
    this._pendingHeyGenMessage = null;
    if (this.audioBridge) {
      try { this.audioBridge.disconnect(); } catch (_) {}
      this.audioBridge = null;
      this.realtimeHandler.audioBridge = null;
    }
    // LITE disabled the speaker path once the AI stream arrived — restore it
    this.mixer.enableAI();
    if (!this.museumAudioPlayer?.isPlaying) this.mixer.unmuteAI();
    manager.disconnect().catch(() => {}); // also stops the LiveAvatar session if one was created
    this._fireEvent('heygen:unavailable', reason);
    if (this.connection.isInit()) {
      // output_modalities now resolves to ['audio'] (no avatar)
      this.sendSessionUpdate();
    }
  }

  setSceneId(sceneId) {
    this.state.setCurrentScene(sceneId);
  }

  async connect(container = document.body) {
    this._avatarContainer = container;
    this.mixer.resume();
    if (this.isMuseum()) {
      const firstSceneId = this._getFirstGuideSceneId();
      if (firstSceneId && firstSceneId !== this.state.currentScene) {
        this._fireEvent('on-change-scene', { room_id: firstSceneId });
        await Promise.race([
          new Promise(resolve => this.once('scene:ready', resolve)),
          new Promise(resolve => setTimeout(resolve, 10000)),
        ]);
      }
    }
    // Full session config goes into /calls. It is built at the last moment
    // (after token, mic and SDP offer are ready) so the video-avatar result is
    // known: a failed avatar means audio output instead of silent text output.
    this.connection.sessionConfig = null;
    this.connection.beforeCalls = async () => {
      await this._settleHeyGen();
      this.connection.sessionConfig = this._buildGASessionConfig();
      // Only this first payload may carry the greeting instruction; every later
      // session.update (scene change, RAG refresh) must suppress it instead.
      this.state.markSessionStarted();
      console.log(`[heygen][mode] OpenAI session output_modalities=${JSON.stringify(this.connection.sessionConfig.output_modalities)}` +
        (this.heygenManager ? ` (avatar mode: ${this.heygenMode})` : ' (no avatar)'));
    };
    this._sessionStartTime = performance.now();
    await this.connection.connect();
  }

  async connectMuseumOnly(container = document.body) {
    this._avatarContainer = container;
    this.mixer.resume();
    const firstSceneId = this._getFirstGuideSceneId();
    if (firstSceneId && firstSceneId !== this.state.currentScene) {
      this._fireEvent('on-change-scene', { room_id: firstSceneId });
      await Promise.race([
        new Promise(resolve => this.once('scene:ready', resolve)),
        new Promise(resolve => setTimeout(resolve, 10000)),
      ]);
    }
    this._sessionStartTime = performance.now();
    this._startGuideService(this._avatarContainer);
  }

  _getFirstGuideSceneId() {
    const guide = this.state.tourData?.guide || [];
    if (!guide.length) return null;
    const sorted = guide.slice().sort((a, b) => (a.position || 0) - (b.position || 0));
    return sorted[0]?.id || null;
  }

  destroy() {
    if (this.heygenManager) this.heygenManager.interrupt();
    this.disconnect();
  }

  disconnect() {
    const t = this.statusDisplay.totalStats;
    if (t.total > 0) {
      const ct = this.statusDisplay.calculateCost(t);
      const wallMin = this._sessionStartTime
        ? ((performance.now() - this._sessionStartTime) / 60000).toFixed(2)
        : 'n/a';
      const audioMin = (this._totalAudioSeconds / 60).toFixed(2);
      const costPerAudioMin = this._totalAudioSeconds > 0
        ? (ct.totalCost / (this._totalAudioSeconds / 60)).toFixed(5)
        : 'n/a';
      console.log(
        `[priceplan] session_end | wall_min=${wallMin} audio_min=${audioMin} | ` +
        `total_tok=${t.total} audio_tok=${t.input_audio + t.output_audio} text_tok=${t.input_text + t.output_text} cached_text=${t.cached_text} | ` +
        `total_cost=$${ct.totalCost.toFixed(5)} audio_cost=$${ct.costAudio.toFixed(5)} text_cost=$${ct.costText.toFixed(5)} | ` +
        `cost_per_audio_min=$${costPerAudioMin}`
      );
    }
    clearTimeout(this._resumeTimer);
    clearTimeout(this._idleTimer);
    this._idleTimer = null;
    if (this._sceneEndPopup) {
      this._sceneEndPopup.destroy();
      this._sceneEndPopup = null;
    }
    if (this.guideService) {
      this.guideService.stop();
      this.guideService = null;
    }
    if (this._museumTimeline) {
      this._museumTimeline.destroy();
      this._museumTimeline = null;
    }
    document.body.classList.remove('museum-active');
    this._setPannellumControls(true);
    if (this.audioBridge) {
      this.audioBridge.disconnect();
      this.audioBridge = null;
      this.realtimeHandler.audioBridge = null;
    }
    if (this.heygenManager) {
      this.heygenManager.disconnect().catch(() => {});
      this.heygenManager = null;
      this.realtimeHandler.heygenManager = null;
    }
    this._heygenConnecting = null;
    this.statusDisplay.destroy();
    this.stopRoomAudio();
    this.museumAudioPlayer.stop();
    this.connection.disconnect();
    this.mixer.destroy();
  }

  stop() {
    if (!this.realtimeHandler.lastMsgObj) return;
    const types = ['response.cancel', 'output_audio_buffer.clear', 'input_audio_buffer.clear'];
    types.forEach(t => this.connection.dataChannel.send(JSON.stringify({ type: t })));
  }

  async say(text) {
    if (!this.connection.isInit() || !text || !text.trim()) return;
    this.sendMoveRoom();
    await this._prepareAnd(() => this._say(text));
  }

  async play(link) {
    const resolved = resolveLink(link, this.bridge.getLanguage(), this.state.tourData?.defaultLang || 'en');
    if (!this.connection.isInit() || !resolved || !resolved.trim() || !this.readyForPlay()) return;
    this.sendMoveRoom();
    await this._prepareAnd(() => this.playRoomAudio(resolved));
  }

  async generateResponce(room_id, call_id = '') {
    if (!this.connection.isInit()) return;
    await this._prepareAnd(() => this._generateResponce(room_id, call_id));
  }

  // ─── Session ─────────────────────────────────────────────────────────────────

  _buildGASessionConfig(promptText = null) {
    const voice = this.state.tourData.ttsModel || 'alloy';
    const VALID_VOICES = new Set(['alloy', 'ash', 'ballad', 'coral', 'echo', 'sage', 'shimmer', 'verse', 'marin', 'cedar']);
    const mic = getMicPreset(this.state.tourData?.micSensitivity);
    this.realtimeHandler.setMicPreset(mic);
    const textOut = this.heygenManager && this.heygenMode !== 'LITE';
    return {
      type: 'realtime',
      // FULL avatar: text out, platform TTS speaks it.
      // LITE avatar: audio out, bridged to the avatar for lip-sync.
      output_modalities: textOut ? ['text'] : ['audio'],
      instructions: (promptText || this.buildSessionInstructions()) + (textOut ? '' : this._accentLine()),
      tools: this.getNavigationTools(),
      tool_choice: 'auto',
      audio: {
        output: { voice: VALID_VOICES.has(voice) ? voice : 'alloy' },
        input: {
          transcription: this._transcription(),
          noise_reduction: { type: mic.noiseReduction },
          turn_detection: this._turnDetection(mic)
        }
      }
    };
  }

  // OpenAI voices have no accent setting — the accent picked in the admin
  // avatar modal (avatar_json.voiceAccent) is asked for in the instructions.
  // Same list as the admin preview (avatar/admin tts_manager ACCENTS).
  _accentLine() {
    const ACCENTS = {
      british: 'British (Received Pronunciation)',
      american: 'General American',
      australian: 'Australian',
      irish: 'Irish',
      scottish: 'Scottish'
    };
    const accent = ACCENTS[this.state.tourData?.voiceAccent];
    return accent ? `\nVOICE: Speak with a natural ${accent} accent.` : '';
  }

  // Pin ASR to the tour's language: left to guess, short English questions came
  // back as Turkish/Russian ("Eskenişme mi havayı?") and the guide acted on them.
  // Only codes the transcriber knows are passed (an unknown one fails the whole
  // session.update); tour codes like 'ua'/'cn' are mapped, anything else keeps
  // auto-detect.
  _transcription() {
    const ALIASES = { ua: 'uk', cn: 'zh', jp: 'ja', kr: 'ko', gr: 'el', se: 'sv', dk: 'da', cz: 'cs', il: 'he' };
    const SUPPORTED = new Set(('af ar hy az be bs bg ca zh hr cs da nl en et fi fr gl de el he hi hu is id it ' +
      'ja kn kk ko lv lt mk ms mr mi ne no fa pl pt ro ru sr sk sl es sw sv tl ta th tr uk ur vi cy').split(' '));
    const raw = `${this.state.language || ''}`.toLowerCase().split(/[-_]/)[0];
    const lang = ALIASES[raw] || raw;
    return SUPPORTED.has(lang)
      ? { model: 'gpt-4o-mini-transcribe', language: lang }
      : { model: 'gpt-4o-mini-transcribe' };
  }

  // ?avatarEagerness=low|medium|high|auto overrides how long semantic VAD
  // waits before deciding the visitor has finished — for trying a faster
  // turn end on one tour. 'low' (the preset) waits longest.
  _turnDetection(mic) {
    const td = { ...mic.turnDetection };
    const eagerness = this._urlParams.avatarEagerness;
    if (td.type === 'semantic_vad' && ['low', 'medium', 'high', 'auto'].includes(eagerness)) {
      td.eagerness = eagerness;
    }
    return td;
  }

  sendSessionUpdate(promptText = null) {
    if (!this.connection.isInit()) return;
    const session = this._buildGASessionConfig(promptText);
    const sig = JSON.stringify(session);
    if (this._lastSessionSig === sig) return;
    this._lastSessionSig = sig;
    const instrLen = session.instructions?.length || 0;
    const toolsLen = JSON.stringify(session.tools || []).length;
    console.log(`[tokens] session.update | instructions=${instrLen} chars (~${Math.round(instrLen / 4)} tokens) tools=${toolsLen} chars (~${Math.round(toolsLen / 4)} tokens)`);
    this.connection.dataChannel.send(JSON.stringify({ type: 'session.update', session }));
    console.log('[guide] Session instructions:', session.instructions);
  }

  buildSessionInstructions(promptText = null) {
    return this.sessionBuilder.build(this.bridge.getLanguage(), promptText);
  }

  getNavigationTools() {
    // Shared tools used by both tour types
    const sharedTools = [
      { type: 'function', name: 'on_change_scene', description: 'Navigate to a room. Use the R_ID value from the script as room_id.', parameters: { type: 'object', properties: { room_id: { type: 'string', description: 'R_ID from the script, e.g. "19549"' }, room_name: { type: 'string' }, question: { type: 'string', description: 'Only when the visitor ALSO asked a question about where they are going ("what is the flooring like in the bedroom, and take me there"): that question, in their words. It is answered automatically once the room has loaded.' } }, required: ['room_id', 'room_name'] } },
      { type: 'function', name: 'get_current_location', parameters: { type: 'object', properties: {} } },
      { type: 'function', name: 'get_audio_status', description: 'Check whether the AI voice or museum audio is currently playing. Returns { ai_speaking, audio_playing, idle }.', parameters: { type: 'object', properties: {} } },
      { type: 'function', name: 'on_close', parameters: { type: 'object', properties: {} } },
      { type: 'function', name: 'search_knowledge_base', description: 'Search the external RAG knowledge base when existing context is insufficient.', parameters: { type: 'object', properties: { query: { type: 'string', description: 'The user question or query to search in the knowledge base.' } }, required: ['query'] } },
      { type: 'function', name: 'on_change_language', description: 'Update the current language used by the tour guide (e.g. en, es, fr).', parameters: { type: 'object', properties: { language: { type: 'string', description: "Language code for the next replies, for example 'en', 'es', 'fr'." } }, required: ['language'] } },
    ];

    if (this.isMuseum()) {
      // Museum mode: guide service handles navigation/audio; agent only answers visitor questions
      return [
        { type: 'function', name: 'get_current_location', parameters: { type: 'object', properties: {} } },
        { type: 'function', name: 'search_knowledge_base', description: 'Search the external RAG knowledge base when existing context is insufficient.', parameters: { type: 'object', properties: { query: { type: 'string', description: 'The user question or query to search in the knowledge base.' } }, required: ['query'] } },
        { type: 'function', name: 'on_change_language', description: 'Update the current language used by the tour guide (e.g. en, es, fr).', parameters: { type: 'object', properties: { language: { type: 'string', description: "Language code for the next replies, for example 'en', 'es', 'fr'." } }, required: ['language'] } },
        { type: 'function', name: 'on_close', description: 'End the session. Call ONLY when the visitor explicitly says goodbye, bye, or clearly states they want to leave or stop the tour. Never call this just because a question was answered or the tour script finished.', parameters: { type: 'object', properties: {} } },
      ];
    }

    // Default (real estate / free-roam) mode: full tool set
    return [
      ...sharedTools,
      { type: 'function', name: 'on_open_info', description: 'Navigate to a specific hotspot', parameters: { type: 'object', properties: { hotspot_id: { type: 'string' }, type: { type: 'string' } }, required: ['hotspot_id', 'type'] } },
      { type: 'function', name: 'rotate_view', description: 'Point the camera at an object or area in the current room. Use when the user says "show me", "where is", or simply names something visible in the room (matched from the describe list in the current context). Pass the yaw and pitch from the describe entry.', parameters: { type: 'object', properties: { yaw: { type: 'number', description: 'Horizontal camera angle in degrees' }, pitch: { type: 'number', description: 'Vertical camera angle in degrees' }, hfov: { type: 'number', description: 'Field of view (optional)' }, duration: { type: 'number', description: 'Animation duration ms (optional)' } }, required: ['yaw', 'pitch'] } },
      { type: 'function', name: 'list_available_rooms', parameters: { type: 'object', properties: {} } },
      { type: 'function', name: 'get_position', description: 'Get the current panorama camera position (yaw, pitch).', parameters: { type: 'object', properties: {} } },
      { type: 'function', name: 'on_change_mode', parameters: { type: 'object', properties: { mode: { type: 'string' } }, required: ['mode'] } },
    ];
  }

  _startGuideService(container = document.body) {
    const guide = (this.state.tourData?.guide || []);
    if (!guide.length) return;
    const defaultLang = this.state.tourData?.defaultLang || 'en';

    this.guideService = new MuseumGuideService({
      guide,
      defaultLang,
      bridge: this.bridge,
      audioPlayer: this.museumAudioPlayer,
      onSceneEnd: (done, lastCoord) => {
        const lookAroundAudio = this.state.tourData?.lookAroundAudio || {};
        this._sceneEndPopup = new MuseumSceneEndPopup({
          container,
          onNext: async (wasMini) => {
            this._sceneEndPopup = null;
            this.stop(); // cancel any in-progress AI audio response
            if (this.heygenManager) this.heygenManager.interrupt();
            if (wasMini && lastCoord) {
              try { await this.bridge.rotate({ ...lastCoord, duration: 1500 }); } catch (e) {}
            }
            done();
          },
          onLookAround: () => {
            const lang = this.bridge.getLanguage();
            const urls = lookAroundAudio[lang]
              || lookAroundAudio[this.state.tourData?.defaultLang || 'en']
              || [];
            if (urls.length) {
              this.museumAudioPlayer.play(urls[Math.floor(Math.random() * urls.length)]);
            }
          },
        });
      },
    });

    this.guideService.on('stopped', () => {
      if (this._sceneEndPopup) {
        this._sceneEndPopup.destroy();
        this._sceneEndPopup = null;
      }
      this._fireEvent('render-zone:cancel');
    });

    this.guideService.on('completed', () => {
      this._fireEvent('museum:completed');
      if (this._museumTimeline) this._museumTimeline.hide();
      document.body.classList.remove('museum-active');
      this._setPannellumControls(true);
      if (!this.connection.isInit()) return;
      this._createMessage('system', `The museum tour script has finished. Thank the visitor warmly. Then remain available to answer questions — do NOT call on_close unless the visitor explicitly says goodbye. Use language = ${this.bridge.getLanguage()}.`);
      this.connection.dataChannel.send(JSON.stringify({
        type: 'response.create',
        response: {}
      }));
    });

    // ── Timeline ──────────────────────────────────────────────────────────────
    const showTimeline = this.state.tourData?.museum_show_timeline !== false;

    this._setPannellumControls(false);
    document.body.classList.add('museum-active');
    this._fireEvent('museum:started');

    if (showTimeline) {
      this._museumTimeline = new MuseumTimeline({
        guide,
        container,
        onSeek: (sceneIdx, pointIdx) => this.guideService.seek(sceneIdx, pointIdx),
      });
      this._museumTimeline.show();

      this.guideService.on('point:start', ({ sceneIdx, pointIdx }) => {
        this._museumTimeline.updatePosition(sceneIdx, pointIdx);
        this._museumTimeline.show();
      });

      this.museumAudioPlayer.on('timeupdate', ({ progress }) => {
        this._museumTimeline.updateAudioProgress(progress);
      });
    }

    let savedPos = null;
    try {
      const key = `museum_pos_${this.state.tourData?.tourID}`;
      const raw = sessionStorage.getItem(key);
      if (raw) {
        savedPos = JSON.parse(raw);
        sessionStorage.removeItem(key);
      }
    } catch (_) {}
    this.guideService.start(this.language, savedPos);
  }

  _setPannellumControls(visible) {
    const el = document.querySelector('.pannellum-panel-controls');
    if (el) el.style.display = visible ? '' : 'none';
  }

  // ─── RAG (delegates to RAGManager) ───────────────────────────────────────────

  _ragCtx() {
    return {
      roomName: this.state.getRoomInfo(this.state.currentScene)?.title || '',
      tourType: this.state.tourData?.type || '',
    };
  }

  _prefetchRAG(transcript) {
    const text = (transcript || '').trim();
    if (queryTerms(text).size < RealtimeTourGuide.PREFETCH_MIN_TERMS) return; // "yes", "go to the kitchen"…
    const promise = this.rag.lookup(text, this.state.tourData, this._ragCtx()).catch(() => ({ updated: false }));
    this._ragPrefetch = { text, at: performance.now(), promise };
  }

  // The transcript lookup stands in for the model's query when they ask about
  // the same thing. A follow-up like "how much is it?" becomes "price of River
  // House" in the model's query — no overlap, so it is looked up afresh.
  _takePrefetch(query) {
    const p = this._ragPrefetch;
    this._ragPrefetch = null;
    if (!p || performance.now() - p.at > 15000) return null;
    const q = queryTerms(query);
    if (!q.size) return null;
    const t = queryTerms(p.text);
    const shared = [...q].filter(w => t.has(w)).length;
    return shared / q.size >= RealtimeTourGuide.PREFETCH_MIN_OVERLAP ? p : null;
  }

  async updateRAGContext(text) {
    const t0 = performance.now();
    let result;
    try {
      const prefetch = this._takePrefetch(text);
      if (prefetch) {
        result = await prefetch.promise;
        if (result.contextText) {
          // rag_ms = what the visitor waited after the tool call, not the whole lookup
          result = { ...result, rag_ms: Math.round(performance.now() - t0),
            rag_detail: `${result.rag_detail || ''};prefetch=${result.rag_ms}ms` };
          this.rag.inject(result);
        } else {
          result = null; // transcript found nothing — try the model's query
        }
      }
      if (!result) result = await this.rag.updateContext(text, this.state.tourData, this._ragCtx());
    } catch (err) {
      console.warn('[guide] RAG update failed', err);
      result = { updated: false, rag_ms: Math.round(performance.now() - t0), rag_source: 'error' };
    }
    // Recorded on misses too (rag_source 'none' / 'error') so slow empty
    // lookups show up in the conversation timings, not only the hits.
    if (result.rag_ms != null) {
      this._fireEvent('timing:rag', {
        rag_ms: result.rag_ms,
        rag_source: result.rag_source || (result.updated ? null : 'none'),
        rag_detail: result.rag_detail || null,
      });
    }
    return result;
  }

  // ─── Room audio ───────────────────────────────────────────────────────────────

  playRoomAudio(link) {
    try {
      if (!link || typeof window === 'undefined') return;
      if (this._roomAudio) {
        try { this._roomAudio.pause(); } catch (_) {}
        try { this._roomAudio.currentTime = 0; } catch (_) {}
      }
      this._roomAudio = new Audio(link);
      this._roomAudio.play()
        .then(() => console.log('🎵 Room audio: playing'))
        .catch(err => {
          const room = this.state.getRoomInfo();
          const text = resolveRoomText(room, this.bridge.getLanguage(), this.state.tourData?.defaultLang || 'en');
          if (text) {
            if (this.heygenManager?.connected) {
              this.heygenManager.speak(text);
            } else {
              this.say(text);
            }
          } else {
            this.generateResponce();
          }
          console.error('🎵 Room audio: play() failed', err);
        });
    } catch (err) {
      console.error('🎵 Room audio: unexpected error', err);
    }
  }

  stopRoomAudio() {
    try {
      if (!this._roomAudio) return;
      this._roomAudio.pause();
      this._roomAudio.currentTime = 0;
    } catch (_) {} finally {
      this._roomAudio = null;
      this._roomAudioPausedBySystem = false;
    }
  }

  // ─── Messaging ────────────────────────────────────────────────────────────────

  sendMoveRoom(room_id) {
    const room = this.state.getRoomInfo(room_id);
    this._createMessage('user', `We are MOVED TO THE ROOM ${room.title}. ${room_id ? 'RoomId ' + room_id : ''}`);
  }

  _say(text) {
    if (!this.connection.isInit() || !text.trim()) return;
    this._createMessage('user', text);
    this.connection.dataChannel.send(JSON.stringify({
      type: 'response.create',
      response: { instructions: `Repeat exactly, natural, without saying that its translation, what the user just said and translate to user language = ${this.bridge.getLanguage()}` }
    }));
  }

  _generateResponce(room_id, call_id = '') {
    this.sendMoveRoom(room_id);
    this.connection.dataChannel.send(JSON.stringify({
      type: 'response.create',
      response: { instructions: `Use user language = ${this.bridge.getLanguage()}` }
    }));
  }

  _createMessage(role, text) {
    try {
      if (!this.connection.isInit() || !text) return;
      const dc = this.connection.dataChannel;
      const prefix = role === 'system' ? 'sys' : 'usr';
      const id = `${prefix}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      console.log(`[guide]: ${role} - ${text}`);
      dc.send(JSON.stringify({
        type: 'conversation.item.create',
        item: { id, type: 'message', role, content: [{ type: 'input_text', text }] }
      }));
      this._historyItems.push({ id, role });
      this._pruneHistory();
    } catch (_) {}
  }

  _pruneHistory() {
    try {
      const dc = this.connection.dataChannel;
      while (this._historyItems.length > this.MAX_HISTORY) {
        const oldest = this._historyItems.shift();
        if (oldest?.id) dc.send(JSON.stringify({ type: 'conversation.item.delete', item_id: oldest.id }));
      }
    } catch (_) {}
  }

  async _prepareAnd(fn) {
    if (!this.connection.isInit()) return;
    this.stopRoomAudio();
    this.stop();
    await new Promise(resolve => setTimeout(resolve, 100));
    await fn();
  }
}

export default RealtimeTourGuide;
